// Owner: record-runtime; server undo keys by interaction-kit (fix round 1). Keyboard commands of a
// record (plan §4.2): `[` and `]` open the previous and next record of the list the member came from,
// `1`…`9` switch tabs, `i` shows or hides the context panel at medium widths, ⌘Z / ⌘⇧Z revert the
// record changes made here through their server undo tokens. A list scope: single keys pause while
// typing and under dialogs; the peek has its own scope (it is a layer). Discovered by glob.
import { defineCommand, defineScope, serverUndoCommands } from '@dataroom/interaction';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';

export type RecordScopeContext = {
  /** Open the record `delta` away in the list context (false when there is none). */
  step?: (delta: 1 | -1) => boolean;
  canStep?: (delta: 1 | -1) => boolean;
  /** Open the n-th tab (1-based); false when there is none. */
  tab?: (n: number) => boolean;
  /** Show or hide the context panel (only at medium widths). */
  toggleContext?: () => boolean;
  /** Copy the record's link (the peek's ⌘C when nothing else is copied). */
  copyLink?: () => boolean;
};

export const recordScope = defineScope<RecordScopeContext | undefined>({
  id: 'records.record',
  kind: 'list',
  title: () => t('interaction.scope.records'),
});

/** The record peek (a layer): previous and next record of its list. */
export const recordPeekScope = defineScope<RecordScopeContext | undefined>({
  id: 'records.peek',
  kind: 'layer',
  title: () => t('records.peek.scope'),
});

/** `[` / `]` of a scope; the peek's shadow the page's (the peek is a layer above the page). */
const navigation = (scope: typeof recordScope, prefix: string, overrides?: string) => [
  defineCommand<RecordScopeContext | undefined>({
    id: `${prefix}.previous`,
    title: () => t('records.cmd.previous'),
    icon: 'chevron-up',
    shortcut: '[',
    scope,
    ...(overrides ? { overrides: [`${overrides}.previous`] } : {}),
    when: (ctx) => Boolean(ctx?.canStep?.(-1)),
    run: (ctx) => ctx?.step?.(-1) ?? false,
  }),
  defineCommand<RecordScopeContext | undefined>({
    id: `${prefix}.next`,
    title: () => t('records.cmd.next'),
    icon: 'chevron-down',
    shortcut: ']',
    scope,
    ...(overrides ? { overrides: [`${overrides}.next`] } : {}),
    when: (ctx) => Boolean(ctx?.canStep?.(1)),
    run: (ctx) => ctx?.step?.(1) ?? false,
  }),
];

const tabCommands = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) =>
  defineCommand<RecordScopeContext | undefined>({
    id: `records.record.tab${n}`,
    title: () => t('records.cmd.tab', { n }),
    shortcut: String(n),
    scope: recordScope,
    group: () => t('records.cmd.tabs'),
    hidden: n > 1,
    when: (ctx) => Boolean(ctx?.tab),
    run: (ctx) => ctx?.tab?.(n) ?? false,
  }),
);

export default defineAppCommands({
  id: 'records.record',
  loadTitles: () => import('../../messages-ready/records'),
  scopes: [recordScope, recordPeekScope],
  commands: [
    ...serverUndoCommands(recordScope),
    ...navigation(recordScope, 'records.record'),
    ...navigation(recordPeekScope as typeof recordScope, 'records.peek', 'records.record'),
    ...tabCommands,
    defineCommand<RecordScopeContext | undefined>({
      id: 'records.peek.copyLink',
      title: () => t('records.copyLink'),
      icon: 'link',
      shortcut: 'mod+c',
      scope: recordPeekScope as typeof recordScope,
      when: (ctx) => Boolean(ctx?.copyLink),
      run: (ctx) => ctx?.copyLink?.() ?? false,
    }),
    defineCommand<RecordScopeContext | undefined>({
      id: 'records.record.context',
      title: () => t('records.cmd.context'),
      icon: 'columns',
      shortcut: 'i',
      scope: recordScope,
      when: (ctx) => Boolean(ctx?.toggleContext),
      run: (ctx) => ctx?.toggleContext?.() ?? false,
    }),
  ],
});
